"use client";

import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
  Clock,
  ArrowUpDown,
} from "lucide-react";
import { UserPolicyDetails } from "./UserPolicyDetails";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface UserReportsTableProps {
  users: any[];
}

export function UserReportsTable({ users }: UserReportsTableProps) {
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Toggle expanded user
  const toggleUserExpand = (userId: string) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
    }
  };

  // Handle sorting
  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    let filteredData = users.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.name.toLowerCase().includes(searchLower) ||
        user.role.toLowerCase().includes(searchLower) ||
        user.status.toLowerCase().includes(searchLower)
      );
    });
    
    // Apply sorting
    if (sortConfig !== null) {
      filteredData = [...filteredData].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredData;
  }, [users, searchQuery, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Generate pagination numbers
  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    // Always show first page
    items.push(
      <PaginationItem key="first">
        <PaginationLink 
          onClick={() => setCurrentPage(1)}
          isActive={currentPage === 1}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );
    
    // Show ellipsis if needed
    if (currentPage > 3) {
      items.push(
        <PaginationItem key="ellipsis-start">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Show pages around current
    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);
    
    for (let i = startPage; i <= endPage; i++) {
      if (i > 1 && i < totalPages) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink 
              onClick={() => setCurrentPage(i)}
              isActive={currentPage === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    }
    
    // Show ellipsis if needed
    if (currentPage < totalPages - 2 && totalPages > 3) {
      items.push(
        <PaginationItem key="ellipsis-end">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      items.push(
        <PaginationItem key="last">
          <PaginationLink 
            onClick={() => setCurrentPage(totalPages)}
            isActive={currentPage === totalPages}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };

  // Get status badge
  const getStatusBadge = (status: string, color: string) => {
    const variants: Record<string, any> = {
      success: {
        variant: "outline",
        className: "border-green-500 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        icon: <Check className="h-3.5 w-3.5 mr-1" />
      },
      warning: {
        variant: "outline", 
        className: "border-orange-500 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400",
        icon: <Clock className="h-3.5 w-3.5 mr-1" />
      },
      destructive: {
        variant: "outline",
        className: "border-red-500 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400",
        icon: <AlertTriangle className="h-3.5 w-3.5 mr-1" />
      }
    };
    
    const badgeProps = variants[color] || variants.destructive;
    
    return (
      <Badge variant={badgeProps.variant} className={badgeProps.className}>
        <span className="flex items-center">
          {badgeProps.icon}
          {status}
        </span>
      </Badge>
    );
  };

  return (
    <div className="w-full">
      <div className="p-4 flex items-center justify-between">
        <div className="flex-1 max-w-sm">
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            className="max-w-sm"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {/* {filteredUsers.length > 0 ? (
            <>Row {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredUsers.length)} of {filteredUsers.length}</>
          ) : (
            <>0 rows</>
          )} */}
          Showing {filteredUsers.length > 0 ? startIndex + 1 : 0}-
          {Math.min(startIndex + itemsPerPage, filteredUsers.length)} of{" "}
          {filteredUsers.length} users
        </div>
      </div>

      <Table className="w-full table">
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead 
              className="w-[250px] px-4 py-2 text-xs font-semibold cursor-pointer select-none hover:text-foreground"
              onClick={() => requestSort('name')}
            >
              <div className="flex items-center">
                Name
                <ArrowUpDown className="ml-2 h-4 w-4" />
                {sortConfig?.key === 'name' && (
                  sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽'
                )}
              </div>
            </TableHead>
            <TableHead 
              className="w-[200px] px-4 py-2 text-xs font-semibold cursor-pointer select-none hover:text-foreground"
              onClick={() => requestSort('role')}
            >
              <div className="flex items-center">
                Role
                <ArrowUpDown className="ml-2 h-4 w-4" />
                {sortConfig?.key === 'role' && (
                  sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽'
                )}
              </div>
            </TableHead>
            <TableHead 
              className="text-center w-[150px] px-4 py-2 text-xs font-semibold cursor-pointer select-none hover:text-foreground"
              onClick={() => requestSort('assignedPolicies')}
            >
              <div className="flex items-center justify-center">
                Assigned Policies
                <ArrowUpDown className="ml-2 h-4 w-4" />
                {sortConfig?.key === 'assignedPolicies' && (
                  sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽'
                )}
              </div>
            </TableHead>
            <TableHead 
              className="text-center w-[120px] px-4 py-2 text-xs font-semibold cursor-pointer select-none hover:text-foreground"
              onClick={() => requestSort('readingPercentage')}
            >
              <div className="flex items-center justify-center">
                Reading %
                <ArrowUpDown className="ml-2 h-4 w-4" />
                {sortConfig?.key === 'readingPercentage' && (
                  sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽'
                )}
              </div>
            </TableHead>
            <TableHead 
              className="text-center w-[100px] px-4 py-2 text-xs font-semibold cursor-pointer select-none hover:text-foreground"
              onClick={() => requestSort('quizPercentage')}
            >
              <div className="flex items-center justify-center">
                Quiz %
                <ArrowUpDown className="ml-2 h-4 w-4" />
                {sortConfig?.key === 'quizPercentage' && (
                  sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽'
                )}
              </div>
            </TableHead>
            <TableHead 
              className="w-[150px] px-4 py-2 text-xs font-semibold cursor-pointer select-none hover:text-foreground"
              onClick={() => requestSort('status')}
            >
              <div className="flex items-center">
                Status
                <ArrowUpDown className="ml-2 h-4 w-4" />
                {sortConfig?.key === 'status' && (
                  sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽'
                )}
              </div>
            </TableHead>
            <TableHead className="w-[80px] text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="h-32 text-center">
                No users found
              </TableCell>
            </TableRow>
          ) : (
            paginatedUsers.map((user) => (
              <>
                <TableRow 
                  key={user.id}
                  className={expandedUser === user.id ? "bg-muted/30" : ""}
                >
                  <TableCell 
                    className="font-medium cursor-pointer" 
                    onClick={() => toggleUserExpand(user.id)}
                  >
                    <div className="flex items-center">
                      {expandedUser === user.id ? (
                        <ChevronDown className="h-4 w-4 mr-2 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 mr-2 text-muted-foreground" />
                      )}
                      {user.name}
                    </div>
                  </TableCell>
                  <TableCell 
                    className="cursor-pointer"
                    onClick={() => toggleUserExpand(user.id)}
                  >
                    {user.role}
                  </TableCell>
                  <TableCell 
                    className="text-center cursor-pointer"
                    onClick={() => toggleUserExpand(user.id)}
                  >
                    {user.assignedPolicies}
                  </TableCell>
                  <TableCell 
                    className="text-center cursor-pointer"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-2 w-20 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                        <div 
                          className={`h-full ${user.readingPercentage === 100 ? 'bg-green-500' : 'bg-primary'}`}
                          style={{ width: `${user.readingPercentage}%` }}
                        />
                      </div>
                      <span className="text-sm">{user.readingPercentage}%</span>
                    </div>
                  </TableCell>
                  <TableCell 
                    className="cursor-pointer"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{user.quizPercentage}%</span>
                        <span>{user.completedQuizzes}/{user.totalQuizzes}</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                        <div 
                          className={`h-full ${user.quizPercentage === 100 ? 'bg-green-500' : 'bg-primary'}`}
                          style={{ width: `${user.quizPercentage}%` }}
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleUserExpand(user.id)}
                      className="h-8 w-8 p-0"
                      type="button"
                    >
                      {expandedUser === user.id ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="sr-only">Toggle details</span>
                    </Button>
                  </TableCell>
                </TableRow>
                {expandedUser === user.id && (
                  <TableRow>
                    <TableCell colSpan={7} className="p-0">
                      <UserPolicyDetails user={user} />
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between px-2 py-4">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${itemsPerPage}`}
              onValueChange={(value) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1); // Reset to first page when changing page size
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={itemsPerPage} />
              </SelectTrigger>
              <SelectContent side="top">
                {[5, 10, 20, 30, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {currentPage} of {totalPages || 1}
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>

              {generatePaginationItems()}

              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
